import { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Input, Select, Button, Spinner, Tag,
  Table, Thead, Tbody, Tr, Th, Td, HStack, InputGroup, InputLeftElement,
  IconButton, Tooltip, useToast, Center,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
} from '@chakra-ui/react';
import { Plus, Pencil, Trash2, Send, PauseCircle, Search, Bell, BarChart3, Settings } from 'lucide-react';
import Pagination from 'react-js-pagination';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LuBell } from 'react-icons/lu';
import Header from '../../../components/Header';
import PageHeading from '../../../components/shared/PageHeading';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import { PRIMARY_BG } from '../../../theme/brand';
import usePagePermissions from '../../../hooks/usePagePermissions';
import InAppSettingsModal from './InAppSettingsModal';
import { validateMessageDoc, targetsAllCustomers, ALL_CUSTOMERS_CONFIRM, imageUrlsOf } from './inAppRules';
import { probeImages } from './inAppImageProbe';

const API_URL = `${process.env.REACT_APP_API_URL}/in-app-messages`;
const ACCENT = PRIMARY_BG;
const LIMIT = 10;

const getHeaders = () => ({
  'x-api-key': localStorage.getItem('api-key'),
  'x-access-token': localStorage.getItem('customer-portal'),
});

const STATUS_META = {
  draft:    { label: 'Draft',    bg: '#F4F4F5', fg: '#71717A', dot: '#A1A1AA' },
  active:   { label: 'Active',   bg: '#DCFCE7', fg: '#166534', dot: '#22C55E' },
  paused:   { label: 'Paused',   bg: '#FFEDD5', fg: '#9A3412', dot: '#F97316' },
  archived: { label: 'Archived', bg: '#E4E4E7', fg: '#52525B', dot: '#71717A' },
};

const TYPE_LABEL = {
  modal: 'Modal',
  banner: 'Banner',
  'slide-in': 'Slide-in',
  fullscreen: 'Fullscreen',
  tooltip: 'Tooltip',
  inbox: 'Inbox',
};

const EVENT_LABEL = {
  app_open: 'App open',
  session_start: 'Session start',
  screen_view: 'Screen view',
  screen_time: 'Time on screen',
  custom_event: 'Custom event',
  session_time: 'Time in session',
  app_request: 'App request',
};

// Audience column label — handles all current audience types (all / cohorts / legacy cohort / tags).
const audienceLabel = (a = {}) => {
  const aud = a || {};
  if (aud.type === 'cohorts' || aud.type === 'segments') {
    const names = Array.isArray(aud.cohort_names) ? aud.cohort_names.filter(Boolean) : [];
    if (names.length) return names.join(', ');
    const n = (aud.cohort_ids || []).length;
    return n ? `${n} segment${n > 1 ? 's' : ''}` : 'Segment / Cohort';
  }
  if (aud.type === 'cohort') return aud.cohort_name || 'Segment / Cohort'; // legacy single
  if (aud.type === 'tags') {
    const names = Array.isArray(aud.tag_names) ? aud.tag_names.filter(Boolean) : [];
    if (names.length) return names.join(', ');
    const n = (aud.tag_ids || []).length;
    return n ? `${n} tag${n > 1 ? 's' : ''}` : 'Tags';
  }
  return 'All customers';
};

const TABS = ['All', 'Draft', 'Active', 'Paused', 'Archived'];

const fmt = (dateStr) =>
  dateStr ? moment(dateStr).format('DD MMM YYYY') : '-';

function DeleteConfirmModal({ isOpen, onClose, onConfirm, name, isDeleting }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="15px">Delete message</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="13px" color="gray.600">
            Are you sure you want to delete <b>{name}</b>? This can't be undone.
          </Text>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button size="sm" colorScheme="red" isLoading={isDeleting} onClick={onConfirm}>Delete</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const InAppMessageList = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [messages, setMessages] = useState([]);
  const [summaries, setSummaries] = useState({}); // id → { impressions, ctr, conversion_rate }
  const [pagination, setPagination] = useState({ total: 0, pages: 1, limit: LIMIT });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [pausingId, setPausingId] = useState(null);

  const { loading: featurePermissionsLoading, can } = usePagePermissions('tools/in-app-messages');

  // "in-app-message-list" gates seeing the list at all; "in-app-designer" covers
  // create/edit (opening the designer); delete and publish are their own slugs.
  const inAppMessageListPermission = can("in-app-message-list");
  const inAppMessageCreatePermission = can("in-app-designer");
  const inAppMessageEditPermission = can("in-app-designer");
  const inAppMessageDeletePermission = can("in-app-message-delete");
  const inAppMessagePublishPermission = can("in-app-message-publish");
  const inAppAnalyticsPermission = can("in-app-analytics");
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadMessages = useCallback(async () => {
    if (featurePermissionsLoading || !inAppMessageListPermission) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', LIMIT);
      if (search) params.set('search', search);
      if (activeTab !== 'All') params.set('status', activeTab.toLowerCase());
      const { data: res } = await axios.get(`${API_URL}?${params.toString()}`, { headers: getHeaders() });
      setMessages(res?.data?.messages || []);
      setPagination(res?.data?.pagination || { total: 0, pages: 1, limit: LIMIT });
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, activeTab, featurePermissionsLoading, inAppMessageListPermission]);

  useEffect(() => { void loadMessages(); }, [loadMessages]);

  // Best-effort performance badges (30d) for the visible rows — one batched call to
  // the RRS stats-summary endpoint. Only when the user has the analytics permission.
  useEffect(() => {
    if (!inAppAnalyticsPermission || !messages.length) { setSummaries({}); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const ids = messages.map((m) => m.id).join(',');
        const { data: res } = await axios.get(
          `${API_URL}/stats-summary?ids=${encodeURIComponent(ids)}&range=last_30_days`,
          { headers: getHeaders() },
        );
        if (cancelled) return;
        const map = {};
        for (const s of (res?.data?.summaries || [])) map[s.message_id] = s;
        setSummaries(map);
      } catch {
        /* badges are best-effort — never block the list */
      }
    })();
    return () => { cancelled = true; };
  }, [messages, inAppAnalyticsPermission]);

  // Reset to page 1 whenever the filters change
  useEffect(() => { setPage(1); }, [search, activeTab]);

  const handleEdit = (item) => navigate(`/tools/in-app/${item.id}`);
  const handleCreate = () => navigate('/tools/in-app');

  // F30: publish / pause bump updated_at, and the server lists by updated_at desc, so a reload
  // re-sorted the table under the user's cursor. Update the row in place instead (a row that no
  // longer matches the status tab leaves it); the order refreshes on the next page/filter load.
  const patchRow = (id, nextStatus) => {
    setMessages((ms) => ms
      .map((m) => (m.id === id ? { ...m, status: nextStatus } : m))
      .filter((m) => activeTab === 'All' || m.id !== id || m.status === activeTab.toLowerCase()));
  };

  // P-4 / F15: the row Publish loads the stored message and applies the editor's rules
  // (inAppRules.validateMessageDoc); blockers are listed in a Modal, nothing is published.
  const [blockers, setBlockers] = useState(null); // { item, issues }
  const handlePublish = async (item) => {
    setPublishingId(item.id);
    try {
      const { data: got } = await axios.get(`${API_URL}/${item.id}`, { headers: getHeaders() });
      const doc = got?.data || {};
      // P-8: deep links are checked against the tenant's allowed schemes (settings; [] when unknown).
      let deepLinkSchemes = [];
      let brand = {};
      try {
        const { data: st } = await axios.get(`${process.env.REACT_APP_API_URL}/in-app-settings`, { headers: getHeaders() });
        deepLinkSchemes = Array.isArray(st?.data?.deep_link_schemes) ? st.data.deep_link_schemes : [];
        brand = { brandColor: st?.data?.brand_color || null, brandColorDark: st?.data?.brand_color_dark || null }; // DA-BUG-1055
      } catch { /* older backend / no settings → any custom scheme, as the server does */ }
      const brokenImages = await probeImages(imageUrlsOf(doc), 5000); // F11
      const issues = validateMessageDoc(doc, { deepLinkSchemes, brokenImages, brand });
      if (issues.length) { setBlockers({ item, issues }); return; }
      if (targetsAllCustomers(doc) && !window.confirm(ALL_CUSTOMERS_CONFIRM)) return;
      const { data: pub } = await axios.post(`${API_URL}/${item.id}/publish`, {}, { headers: getHeaders() });
      toast({ title: 'Message published', status: 'success', duration: 2000 });
      patchRow(item.id, pub?.data?.status || 'active');
    } catch {
      toast({ title: "Couldn't publish", status: 'error', duration: 3000 });
    } finally {
      setPublishingId(null);
    }
  };

  // Pause (unpublish) a live message: active → paused, removed from the SDK cache.
  const handlePause = async (item) => {
    setPausingId(item.id);
    try {
      const { data: un } = await axios.post(`${API_URL}/${item.id}/unpublish`, {}, { headers: getHeaders() });
      toast({ title: 'Message paused', status: 'success', duration: 2000 });
      patchRow(item.id, un?.data?.status || 'paused');
    } catch {
      toast({ title: "Couldn't pause", status: 'error', duration: 3000 });
    } finally {
      setPausingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/${deleteTarget.id}`, { headers: getHeaders() });
      toast({ title: 'Deleted', status: 'success', duration: 2000 });
      setDeleteTarget(null);
      void loadMessages();
    } catch {
      toast({ title: "Couldn't delete", status: 'error', duration: 3000 });
    } finally {
      setIsDeleting(false);
    }
  };

  if (featurePermissionsLoading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        </BoxWrapper>
      </>
    );
  }

  if (!inAppMessageListPermission) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                No Access
              </Text>
              <Text fontSize="sm" color="gray.500">
                You don't have permission to view this sections.
              </Text>
            </Box>
          </Center>
        </BoxWrapper>
      </>
    );
  }

  return (
    <>
      <Header />
      <BoxWrapper>
        <Box>
          <Box py={4} borderBottomWidth="1px" mb={5}>
            <PageHeading icon={LuBell} title="In-App Notification" subtitle="Manage the in-app modals, banners, and prompts you've built" mb={0}>
            <HStack spacing={2}>
            {inAppMessageEditPermission && (
              <Button size="sm" variant="outline" leftIcon={<Settings size={14} />} borderRadius="lg" onClick={() => setSettingsOpen(true)}>
                Settings
              </Button>
            )}
            {inAppMessageCreatePermission && (
              <Button
                size="sm"
                bg={ACCENT}
                color="white"
                _hover={{ bg: 'var(--brand-primary-active)' }}
                onClick={handleCreate}
                leftIcon={<Plus size={14} />}
                borderRadius="lg"
              >
                Create New
              </Button>
            )}
            </HStack>
            </PageHeading>
          </Box>
          <InAppSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

          <Flex gap={3} mb={4} alignItems="center">
            <InputGroup flex={1} size="sm">
              <InputLeftElement pointerEvents="none">
                <Search size={15} color="#A0AEC0" />
              </InputLeftElement>
              <Input
                placeholder="Search messages by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="lg"
                bg="white"
              />
            </InputGroup>
            <Select
              size="sm"
              w="160px"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              borderRadius="lg"
              bg="white"
            >
              {TABS.map((tab) => <option key={tab} value={tab}>{tab}</option>)}
            </Select>
          </Flex>

          <Box bg="white" borderRadius="xl" borderWidth="1px" overflow="hidden">
            <Table size="sm">
              <Thead bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                <Tr>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Name</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Type</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Status</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Trigger</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Audience</Th>
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Updated</Th>
                  {inAppAnalyticsPermission && (
                    <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em">Performance · 30d</Th>
                  )}
                  <Th py={3.5} color="gray.500" fontWeight="600" fontSize="11px" textTransform="uppercase" letterSpacing="0.04em" textAlign="right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={inAppAnalyticsPermission ? 8 : 7} textAlign="center" py={16}>
                      <Flex direction="column" alignItems="center" gap={2}>
                        <Spinner size="sm" color={ACCENT} />
                        <Text fontSize="xs" color="gray.400">Loading messages...</Text>
                      </Flex>
                    </Td>
                  </Tr>
                ) : messages.length === 0 ? (
                  <Tr>
                    <Td colSpan={inAppAnalyticsPermission ? 8 : 7} textAlign="center" py={16}>
                      <Flex direction="column" alignItems="center" gap={2}>
                        <Bell color="#CBD5E0" size={32} />
                        <Text fontSize="sm" color="gray.500" fontWeight="medium">No in-app messages yet</Text>
                        <Text fontSize="xs" color="gray.400">Click "Create New" to build your first one</Text>
                      </Flex>
                    </Td>
                  </Tr>
                ) : (
                  messages.map((item, i) => {
                    const statusMeta = STATUS_META[item.status] || STATUS_META.draft;
                    return (
                      <Tr
                        key={item.id}
                        cursor={inAppMessageEditPermission ? 'pointer' : 'default'}
                        _hover={inAppMessageEditPermission ? { bg: 'blue.50' } : {}}
                        onClick={inAppMessageEditPermission ? () => handleEdit(item) : undefined}
                        borderTopWidth={i === 0 ? 0 : '1px'}
                        transition="background 0.1s"
                      >
                        <Td py={4} maxW="220px">
                          <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>{item.name}</Text>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" fontWeight="500" color="gray.700">{TYPE_LABEL[item.message_type] || item.message_type}</Text>
                        </Td>
                        <Td py={4}>
                          <Tag size="sm" borderRadius="full" display="inline-flex" alignItems="center" gap={1.5} bg={statusMeta.bg} color={statusMeta.fg} px={2.5} py={1} fontSize="xs" fontWeight="600">
                            <Box w={1.5} h={1.5} borderRadius="full" bg={statusMeta.dot} flexShrink={0} />
                            {statusMeta.label}
                          </Tag>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.600">{EVENT_LABEL[item.trigger?.event] || item.trigger?.event || '-'}</Text>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.600" noOfLines={1} maxW="160px">
                            {audienceLabel(item.audience)}
                          </Text>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.600">{fmt(item.updated_at)}</Text>
                        </Td>
                        {inAppAnalyticsPermission && (
                          <Td py={4}>
                            {(() => {
                              const s = summaries[item.id];
                              if (!s || !s.impressions) return <Text fontSize="xs" color="gray.300">—</Text>;
                              return (
                                <HStack spacing={2}>
                                  <Tooltip label="Impressions (30d)" hasArrow fontSize="xs">
                                    <Text fontSize="xs" fontWeight="600" color="gray.600">{Number(s.impressions).toLocaleString()}</Text>
                                  </Tooltip>
                                  <Tooltip label="Click-through rate" hasArrow fontSize="xs">
                                    <Tag size="sm" borderRadius="full" bg="blue.50" color="blue.700" fontSize="11px" fontWeight="600">{Number(s.ctr).toFixed(1)}%</Tag>
                                  </Tooltip>
                                  {s.conversion_rate != null && (
                                    <Tooltip label="Conversion rate" hasArrow fontSize="xs">
                                      <Tag size="sm" borderRadius="full" bg="green.50" color="green.700" fontSize="11px" fontWeight="600">{Number(s.conversion_rate).toFixed(1)}%</Tag>
                                    </Tooltip>
                                  )}
                                </HStack>
                              );
                            })()}
                          </Td>
                        )}
                        <Td py={4} onClick={(e) => e.stopPropagation()}>
                          <HStack spacing={1} justify="flex-end">
                            {(item.status === 'draft' || item.status === 'paused') && inAppMessagePublishPermission && (
                              <Tooltip label={item.status === 'paused' ? 'Resume' : 'Publish'} hasArrow fontSize="xs">
                                <IconButton
                                  aria-label={item.status === 'paused' ? 'Resume' : 'Publish'} icon={publishingId === item.id ? <Spinner size="xs" /> : <Send size={13} />}
                                  size="xs" variant="ghost" color="green.600"
                                  isDisabled={publishingId === item.id}
                                  onClick={() => handlePublish(item)}
                                />
                              </Tooltip>
                            )}
                            {item.status === 'active' && inAppMessagePublishPermission && (
                              <Tooltip label="Pause" hasArrow fontSize="xs">
                                <IconButton
                                  aria-label="Pause" icon={pausingId === item.id ? <Spinner size="xs" /> : <PauseCircle size={13} />}
                                  size="xs" variant="ghost" color="orange.500"
                                  isDisabled={pausingId === item.id}
                                  onClick={() => handlePause(item)}
                                />
                              </Tooltip>
                            )}
                            {inAppAnalyticsPermission && (
                              <Tooltip label="Analytics" hasArrow fontSize="xs">
                                <IconButton
                                  aria-label="Analytics" icon={<BarChart3 size={13} />}
                                  size="xs" variant="ghost" color="blue.500"
                                  onClick={() => navigate(`/tools/in-app-messages/${item.id}/analytics`)}
                                />
                              </Tooltip>
                            )}
                            {inAppMessageEditPermission && (
                              <Tooltip label="Edit" hasArrow fontSize="xs">
                                <IconButton
                                  aria-label="Edit" icon={<Pencil size={13} />}
                                  size="xs" variant="ghost" color="gray.500"
                                  onClick={() => handleEdit(item)}
                                />
                              </Tooltip>
                            )}
                            {item.status !== 'active' && inAppMessageDeletePermission && (
                              <Tooltip label="Delete" hasArrow fontSize="xs">
                                <IconButton
                                  aria-label="Delete" icon={<Trash2 size={13} />}
                                  size="xs" variant="ghost" color="red.400"
                                  onClick={() => setDeleteTarget(item)}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </Box>

          {pagination.total > LIMIT && (
            <Flex justify="center" mt={5}>
              <Pagination
                activePage={page}
                itemsCountPerPage={pagination.limit || LIMIT}
                totalItemsCount={pagination.total}
                pageRangeDisplayed={5}
                onChange={setPage}
                itemClass="page-item"
                linkClass="page-link"
              />
            </Flex>
          )}
        </Box>

        <DeleteConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          name={deleteTarget?.name}
          isDeleting={isDeleting}
        />
        <Modal isOpen={!!blockers} onClose={() => setBlockers(null)} isCentered size="md">
          <ModalOverlay />
          <ModalContent borderRadius="14px">
            <ModalHeader fontSize="16px">Can't publish "{blockers?.item?.name}" yet</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" color="gray.600" mb={2}>Fix these in the editor first:</Text>
              <Box as="ul" pl={5} fontSize="sm" color="red.600" data-testid="publish-blockers">
                {(blockers?.issues || []).map((m, i) => <li key={`${i}-${m}`}>{m}</li>)}
              </Box>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button size="sm" variant="ghost" onClick={() => setBlockers(null)}>Close</Button>
              {inAppMessageEditPermission && (
                <Button size="sm" colorScheme="blue" onClick={() => { const it = blockers.item; setBlockers(null); handleEdit(it); }}>Open in editor</Button>
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
      </BoxWrapper>
    </>
  );
};

export default InAppMessageList;
